import { useEffect, useState } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import {
  useSensors,
  useSensor,
  PointerSensor,
  KeyboardSensor,
  DndContext,
  closestCorners,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  DragOverlay,
  DropAnimation,
  defaultDropAnimation,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates, arrayMove } from "@dnd-kit/sortable";
import { BoardSections as BoardSectionsType, Project } from "../types";
import { findBoardSectionContainer, initializeBoard } from "../utils/board";
import BoardSection from "./BoardSection";
import { getProjectById } from "../utils/projects";
import ProjectItem from "./ProjectItem";
import {
  searchProjectsToProjectPortfolio,
  updateProjectPortifolioStatus,
} from "../../../services/api/projects/projectService";
import { IProjectPortfolioFilter } from "../../../@types/project/project-portfolio-filter.type";
import { IProjectPortfolioResponse } from "../../../@types/project/project-portfolio-response.type";
import styles from "./styles.module.scss";
import { ProjectStatusService } from "../../../services/api/projectStatus/ProjectStatusService";
import { IProjectPorfolioStatusUpdate } from "../../../@types/project/project-portfolio-status-update.type";
import usePermission from "../../../hooks/usePermission";
import { FeatureStatic } from "../../../@types/statics/feature.static";
import { storageUserGet } from "../../../storage/storageUser";
import { toast } from "react-toastify";
import { IProjectStatus } from "../../../@types/project/project-status.type";
import CircularProgress from "@mui/material/CircularProgress";

interface ProjectProps {
  searchParamsPortfolio: IProjectPortfolioFilter;
}

export const BoardSectionList: React.FC<ProjectProps> = ({
  searchParamsPortfolio,
}) => {
  const [boardSections, setBoardSections] = useState<BoardSectionsType>(
    {} as BoardSectionsType
  );
  const [activeProjectId, setActiveProjectId] = useState<null | string>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectStatus, setProjectStatus] = useState<IProjectStatus[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const permissions = usePermission(FeatureStatic.projects.id);
  const user = storageUserGet();

  useEffect(() => {
    loadData();
  }, [searchParamsPortfolio]);

  const loadData = async () => {
    if (user.resourceId && permissions.canEditMine())
      searchParamsPortfolio.managersIds = [user.resourceId];

    try {
      setLoading(true);
      const projectData = (await searchProjectsToProjectPortfolio(
        searchParamsPortfolio as IProjectPortfolioFilter
      )) as IProjectPortfolioResponse[];

      const initialProjects = projectData.map(
        (item: IProjectPortfolioResponse) => {
          return {
            id: item.projectId,
            title: item.projectName,
            status: item.statusName,
            managerName: item.managerName,
            projectKey: item.projectKey,
          } as Project;
        }
      );
      setProjects(initialProjects);
      const responseStatus = await ProjectStatusService.getAllProjectStatus();

      const projectStatusVisible = responseStatus?.filter(
        (item) => item.isVisible
      );

      const initialBoardSections = await initializeBoard(
        initialProjects,
        projectStatusVisible
      );
      setProjectStatus(projectStatusVisible);
      setBoardSections(initialBoardSections);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveProjectId(active.id as string);
  };

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    // Find the containers
    if (permissions.canEdit()) {
      const activeContainer = findBoardSectionContainer(
        boardSections,
        active.id as string
      );
      const overContainer = findBoardSectionContainer(
        boardSections,
        over?.id as string
      );

      if (
        !activeContainer ||
        !overContainer ||
        activeContainer === overContainer
      ) {
        return;
      }

      setBoardSections((boardSection) => {
        const activeItems = boardSection[activeContainer];
        const overItems = boardSection[overContainer];

        // Find the indexes for the items
        const activeIndex = activeItems.findIndex(
          (item) => item.id === active.id
        );
        const overIndex = overItems.findIndex((item) => item.id !== over?.id);

        // Create new arrays with minimal operations
        const newActiveItems = [...activeItems];
        newActiveItems.splice(activeIndex, 1); // Remove the active item directly

        const newOverItems = [...overItems];
        newOverItems.splice(overIndex, 0, activeItems[activeIndex]); // Insert the active item

        return {
          ...boardSection,
          [activeContainer]: newActiveItems,
          [overContainer]: newOverItems,
        };
      });
    }
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (permissions.canEdit()) {
      const activeContainer = findBoardSectionContainer(
        boardSections,
        active.id as string
      );
      const overContainer = findBoardSectionContainer(
        boardSections,
        over?.id as string
      );

      if (
        !activeContainer ||
        !overContainer ||
        activeContainer !== overContainer
      ) {
        return;
      }

      const activeIndex = boardSections[activeContainer].findIndex(
        (project) => project.id === active.id
      );
      const overIndex = boardSections[overContainer].findIndex(
        (project) => project.id === over?.id
      );

      if (activeIndex !== overIndex) {
        setBoardSections((boardSection) => ({
          ...boardSection,
          [overContainer]: arrayMove(
            boardSection[overContainer],
            activeIndex,
            overIndex
          ),
        }));
        const projectId = active.id;
        const projectStatusId = projectStatus.find(
          (item) => item.name === active.data.current?.sortable.containerId
        )?.id;
        updateProjectPortifolioStatus({
          projectId: projectId,
          projectStatusId: projectStatusId,
        } as IProjectPorfolioStatusUpdate);
      }

      setActiveProjectId(null);
    } else toast.error("Não há permissão o suficiente.");
  };

  const dropAnimation: DropAnimation = {
    ...defaultDropAnimation,
  };

  const project = activeProjectId
    ? getProjectById(projects, activeProjectId)
    : null;

  return loading ? (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      }}
    >
      <CircularProgress size={100} />
    </div>
  ) : (
    <Container className={styles.projectBoard}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <Grid style={{ flexWrap: "nowrap" }} container spacing={4}>
          {Object.keys(boardSections).map((boardSectionKey) => (
            <Grid
              className={styles.divContainer}
              item
              xs={4}
              key={boardSectionKey}
            >
              <BoardSection
                id={boardSectionKey}
                title={boardSectionKey}
                projects={boardSections[boardSectionKey]}
              />
            </Grid>
          ))}
          <DragOverlay dropAnimation={dropAnimation}>
            {project ? <ProjectItem project={project} /> : null}
          </DragOverlay>
        </Grid>
      </DndContext>
    </Container>
  );
};
